import { createFileRoute } from "@tanstack/react-router";
import { SlidersHorizontal, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { usePublicConfig } from "@/api/queries/config";
import { useSearch, useSearchByName } from "@/api/queries/search";
import { SearchBar } from "@/components/search/SearchBar";
import { SearchFilters } from "@/components/search/SearchFilters";
import { HotFlirtsSlider } from "@/components/shared/HotFlirtsSlider";
import { Pagination } from "@/components/shared/Pagination";
import { ProfileGrid } from "@/components/shared/ProfileGrid";
import { useAuth } from "@/hooks/use-auth";
import { useMediaQuery } from "@/hooks/use-media-query";
import { usePageMeta } from "@/hooks/use-page-meta";
import { useSearchStore } from "@/stores/search-store";

const searchParamsSchema = z.object({
	gender: z.string().optional(),
	ageFrom: z.coerce.number().optional(),
	ageTo: z.coerce.number().optional(),
	country: z.string().optional(),
	zip: z.string().optional(),
	radius: z.coerce.number().optional(),
	isOnline: z.coerce.boolean().optional(),
	page: z.coerce.number().optional(),
	name: z.string().optional(),
});

/**
 * Custom validateSearch: parst bekannte Felder mit Zod, lässt sa_*-Keys unberührt durch.
 * TanStack Router stripped standardmaessig unbekannte Keys wenn ein Zod-Schema direkt
 * verwendet wird. Daher brauchen wir eine Funktion die beides vereint.
 *
 * Rückgabetyp ist bewusst Record<string, unknown>, damit TanStack Router sowohl
 * bekannte als auch dynamische sa_*-Keys akzeptiert.
 */
function validateSearch(input: Record<string, unknown>): Record<string, unknown> {
	const parsed = searchParamsSchema.parse(input);

	// sa_*-Keys direkt übernehmen (als Strings)
	const saKeys: Record<string, string> = {};
	for (const [key, value] of Object.entries(input)) {
		if (key.startsWith("sa_") && value !== undefined && value !== "") {
			saKeys[key] = String(value);
		}
	}

	return { ...parsed, ...saKeys };
}

export const Route = createFileRoute("/_app/search/")({
	validateSearch,
	component: SearchPage,
});

function SearchPage() {
	const searchParams = Route.useSearch();
	const navigate = Route.useNavigate();
	const { user } = useAuth();
	const { preferences, initializeFromUser, isInitialized } = useSearchStore();
	const { data: config } = usePublicConfig();

	usePageMeta({
		title: config?.seo.defaultTitle,
	});

	const [filtersOpen, setFiltersOpen] = useState(false);
	const isMobile = useMediaQuery("(max-width: 1023px)");

	// Einmalig Defaults aus User-Profil initialisieren
	useEffect(() => {
		if (user && !isInitialized) {
			initializeFromUser(user.searchingFor, user.age);
		}
	}, [user, isInitialized, initializeFromUser]);

	// Aktive Filter: URL-Params > Store-Defaults + sa_* Params
	const activeFilters: Record<string, unknown> = useMemo(() => {
		const base: Record<string, unknown> = {
			gender:
				(searchParams.gender as string | undefined) ??
				(isInitialized ? preferences.gender || "m+f" : undefined),
			ageFrom:
				(searchParams.ageFrom as number | undefined) ??
				(isInitialized ? preferences.ageFrom : undefined),
			ageTo:
				(searchParams.ageTo as number | undefined) ??
				(isInitialized ? preferences.ageTo : undefined),
			country:
				(searchParams.country as string | undefined) ??
				(isInitialized ? preferences.country : undefined),
			zip: searchParams.zip as string | undefined,
			radius: searchParams.radius as number | undefined,
			isOnline: searchParams.isOnline as boolean | undefined,
			page: searchParams.page as number | undefined,
		};

		// sa_*-Keys aus searchParams übernehmen
		for (const [key, value] of Object.entries(searchParams)) {
			if (key.startsWith("sa_") && value !== undefined && value !== "") {
				base[key] = value;
			}
		}

		return base;
	}, [searchParams, preferences, isInitialized]);

	const isNameSearch = !!(searchParams.name as string | undefined);

	// Query: Filtersuche ODER Namenssuche
	const filterSearch = useSearch(isNameSearch ? {} : activeFilters);
	const nameSearch = useSearchByName(
		(searchParams.name as string) ?? "",
		searchParams.page as number | undefined,
	);
	const searchResult = isNameSearch ? nameSearch : filterSearch;

	const handleFilterChange = (updates: Record<string, unknown>) => {
		navigate({
			search: (prev: Record<string, unknown>) => ({ ...prev, ...updates, page: undefined }),
			replace: true,
		});
	};

	const handlePageChange = (page: number) => {
		navigate({
			search: (prev: Record<string, unknown>) => ({ ...prev, page }),
			replace: true,
		});
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const handleNameSearch = (name: string) => {
		navigate({
			search: { name },
			replace: true,
		});
	};

	const handleNameClear = () => {
		navigate({
			search: (prev: Record<string, unknown>) => {
				const { name: _name, page: _page, ...rest } = prev;
				return rest;
			},
			replace: true,
		});
	};

	const profiles = searchResult.data?.profiles ?? [];
	const totalResults = searchResult.data?.results ?? 0;
	const perPage = searchResult.data?.perPage ?? 20;
	const currentPage = (searchParams.page as number) ?? 1;
	const lastPage = perPage > 0 ? Math.ceil(totalResults / perPage) : 1;

	// Anzahl aktiver Filter (für Badge): statische + dynamische sa_* Parameter
	const activeFilterCount = useMemo(() => {
		let count = 0;
		if (activeFilters.gender) count++;
		if (activeFilters.ageFrom) count++;
		if (activeFilters.ageTo) count++;
		if (activeFilters.country) count++;
		if (activeFilters.zip) count++;
		if (activeFilters.radius) count++;
		if (activeFilters.isOnline) count++;
		// sa_*-Parameter zaehlen
		for (const key of Object.keys(activeFilters)) {
			if (key.startsWith("sa_") && activeFilters[key]) {
				count++;
			}
		}
		return count;
	}, [activeFilters]);

	const filterPanel = <SearchFilters filters={activeFilters} onFilterChange={handleFilterChange} />;

	return (
		<div className="space-y-6">
			{/* Suchleiste */}
			<div>
				<SearchBar
					initialValue={searchParams.name as string | undefined}
					onSearch={handleNameSearch}
					onClear={handleNameClear}
				/>
				{isNameSearch && (
					<p className="mt-2 text-base text-text-secondary">
						Namenssuche nach &ldquo;{searchParams.name as string}&rdquo; &mdash;{" "}
						<button
							type="button"
							onClick={handleNameClear}
							className="text-primary hover:underline cursor-pointer"
						>
							Zur Filtersuche wechseln
						</button>
					</p>
				)}
			</div>

			<div className="flex gap-6 items-start">
				{/* Filter-Sidebar (Desktop) */}
				{!isMobile && !isNameSearch && (
					<aside className="w-[280px] shrink-0 sticky top-24">
						<div className="glass rounded-xl p-5">
							<h2 className="text-base font-semibold text-text mb-4">Filter</h2>
							{filterPanel}
						</div>
					</aside>
				)}

				{/* Ergebnis-Bereich */}
				<div className="flex-1 min-w-0 space-y-6">
					{/* Mobile Filter Toggle */}
					{isMobile && !isNameSearch && (
						<div>
							<button
								type="button"
								onClick={() => setFiltersOpen(!filtersOpen)}
								className="
									inline-flex items-center gap-2 px-4 py-2 rounded-lg
									text-base font-medium transition-all cursor-pointer
									focus-ring
									bg-surface border border-border hover:border-primary/50
									text-text-secondary hover:text-text
								"
							>
								<SlidersHorizontal size={16} />
								<span>Filter</span>
								{activeFilterCount > 0 && (
									<span className="inline-flex items-center justify-center size-5 rounded-full bg-primary text-white text-xs font-bold">
										{activeFilterCount}
									</span>
								)}
							</button>

							{filtersOpen && (
								<div className="mt-3 glass rounded-xl p-5 relative">
									<div className="flex items-center justify-between mb-4">
										<h2 className="text-base font-semibold text-text">Filter</h2>
										<button
											type="button"
											onClick={() => setFiltersOpen(false)}
											className="p-1 rounded-lg text-text-muted hover:text-text hover:bg-surface-dim transition-colors cursor-pointer"
											aria-label="Filter schließen"
										>
											<X size={18} />
										</button>
									</div>
									{filterPanel}
								</div>
							)}
						</div>
					)}

					{/* Hot Flirts */}
					<HotFlirtsSlider />

					{/* Ergebnis-Header */}
					{searchResult.data && (
						<div className="flex items-center justify-between">
							<p className="text-base text-text-secondary">
								{totalResults} {totalResults === 1 ? "Profil" : "Profile"} gefunden
							</p>
						</div>
					)}

					{/* Profil-Grid */}
					<ProfileGrid
						profiles={profiles}
						isLoading={searchResult.isLoading}
						emptyMessage={
							isNameSearch ? "Kein Benutzer mit diesem Namen gefunden" : "Keine Profile gefunden"
						}
					/>

					{/* Pagination */}
					{lastPage > 1 && (
						<div className="pt-4">
							<Pagination
								currentPage={currentPage}
								lastPage={lastPage}
								onPageChange={handlePageChange}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
